import React, { useCallback, Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import window from 'window-or-global';
import { ReactReduxContext } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import { nativeEditorComponentsPreloader } from '@confluence-classic/confluence-fabric-editor/src/lib/index.ts';
import ChangeboardingManager from '@confluence-classic/confluence-changeboarding/src/ChangeboardingManager.js'; // 240KB
import LegacyBridge from '@confluence-classic/confluence-utils/src/components/LegacyBridge/LegacyBridgeContainer.js';

import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';
import { addUFOCustomCohortData } from '@atlaskit/react-ufo/custom-cohort-data';

import { PostOfficeRouterListener } from '@atlassian/confluence-post-office-provider';
import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';

import { ConfluenceConversationAssistant } from '@confluence/conversation-assistant/entry-points/main.ts';
import { SSRRenderFailureNotification } from '@confluence/ssr-render-failure-notification';
import { ConnectSupport } from '@confluence/connect-support';
import { RouteManager } from '@confluence/route-manager/entry-points/RouteManager.ts';
import { useNavigationPolicy } from '@confluence/route-manager/entry-points/useNavigationPolicy.ts';
import { isViewPageLite } from '@confluence/view-page/entry-points/isViewPageLite.ts';
import { namedRoutes } from '@confluence/named-routes/entry-points/named-routes.ts';
import { isEditPage } from '@confluence/named-routes/entry-points/routes-utils.ts';
import { PDF_EXPORT_PAGE } from '@confluence/named-routes/entry-points/routes.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import {
	EMBEDDED_EDIT_FMP_ACTION,
	EMBEDDED_EDIT_TTI_ACTION,
} from '@confluence/browser-metrics/entry-points/metric-keys.ts';
import { TimeToAppIdle } from '@confluence/browser-metrics/entry-points/TimeToAppIdle.ts';
import { BMEventBus } from '@confluence/browser-metrics/entry-points/BMEventBus.ts';
import { markCustomTransitionExperienceForRoute } from '@confluence/browser-metrics/entry-points/utils.ts';
import { startBrowserMetricsPageLoad } from '@confluence/browser-metrics/entry-points/startBrowserMetricsPageLoad.ts';
import { setUniquePageLoadId } from '@confluence/unique-page-load-id/entry-points/UniquePageLoadId.ts';
import { CopySpacePermissionsForLegacyNewSpace } from '@confluence/copy-space-permissions/entry-points/CopySpacePermissionsForLegacyNewSpace.ts';
import { Flags } from '@confluence/flags/entry-points/Flags.ts';
import { CreateDialogContextProvider } from '@confluence/create-dialog-context';
import { Dialogs } from '@confluence/dialogs/entry-points/Dialogs.ts';
import { NetworkStatusListener } from '@confluence/network-status-listener';
import {
	debuggerEnabled,
	clearAnalyticsDebugger,
} from '@confluence/analytics-debugger/entry-points/AnalyticsDebugger.ts';
import { getBrowserHistory, setBrowserHistoryListener } from '@confluence/browser-history';
import { startExperienceForRoute } from '@confluence/experience-tracker/entry-points/startExperienceForRoute.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { releaseSSRedLegacyMacros } from '@confluence/content-renderer-legacy-macros/entry-points/SSRedLegacyMacro.ts';
import { ExternalShareContextProvider } from '@confluence/external-share-context/entry-points/ExternalShareContextProvider.ts';
import { Heartbeat } from '@confluence/heartbeat';
import { EditPageLoadingContextProvider } from '@confluence/editor-loading-context/entry-points/EditPageLoadingContext.ts';
import { LocalStorageQuotaSubscriber } from '@confluence/local-storage-quota-subscriber';
import { useInitializeThemeStateless } from '@confluence/theming/entry-points/useInitializeThemeStateless.ts';
import { NotificationLandedEvent } from '@confluence/notifications/entry-points/NotificationLandedEvent.ts';
import { ExperienceActionMeasures } from '@confluence/action-measures/entry-points/ExperienceActionMeasures.ts';
import { SubscriptionsContextProvider } from '@confluence/graphql/entry-points/SubscriptionsContextProvider.ts';
import { resetApolloStats } from '@confluence/graphql/entry-points/createClient.ts';
import { preloadQueryBasedOnRoute, preloadExtensionsData } from '@confluence/query-preloaders';
import { StrictMode } from '@confluence/strict-mode';
import { useIsDesktop } from '@confluence/desktop-native-bridge';
import { fg } from '@confluence/feature-gating';
import { mobileAppTheme } from '@confluence/mobile-app-detection/entry-points/theme.ts';
import { useIsMobileApp } from '@confluence/mobile-app-detection/entry-points/MobileAppDetection.ts';
import { PreloadingMode } from '@confluence/hover-preloader';
import { getGlobalTransitionPreloader } from '@confluence/route-manager/entry-points/getGlobalTransitionPreloader.ts';
import { QueryBoundaryNotification } from '@confluence/streaming-boundary-error-notification';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { InconsistentLicenseFlag } from '@confluence/inconsistent-license/entry-points/InconsistentLicenseFlag.ts';

import { routeComponents, NotFound } from '../Routes/index.js';
import { LazyCreateDialogQueryRenderer } from '../CreateDialogQueryRenderer/index.js';
import { LazyCreateDialogBlueprintWizardRenderer } from '../PseudoWizardCreateDialog/index.js';
import { preloadLivePagesComponents } from '../../preloaders/index.js';
import SingleModalContainer from '../SingleModal/index.js';
import LegacyCssOverrides from '../../tech-debt/LegacyCssOverrides/index.js';
import ShortcutsProvider from '../KeyboardShortcuts/index.js'; // 0KB
import EngagementMessages from '../EngagementMessages/index.js';
import { preloadComponentsNoStatsigSupport } from '../../route-component-preloader.js';
// This is tangled with fabric editor state that we can't remove for now.
import ErrorContainer from '../ReduxError/src/index.js';

const browserHistory = getBrowserHistory();

/**
 * Note: this wont run on legacy .action routes
 * if you are adding a global component that needs to work on legacy routes, you may need to add it to StandaloneConfluenceNavigation as well
 */

export function App({ sessionData }) {
	window.performance.mark('CFP-63.app-component-render');

	useEffect(() => {
		if (window.__SSR_ERROR__) {
			getMonitoringClient().submitError(JSON.stringify({ ssrError: window.__SSR_ERROR__ }), {
				// Having '-ssr' suffix to not mix with FE Error detector. Set another dedicated cc-fe-performance detector.
				attribution: 'backbone-ssr',
			});
		}
	}, []);

	const { routePolicy } = useNavigationPolicy();
	const { isDesktop } = useIsDesktop();
	const { isMobileApp } = useIsMobileApp();
	const isPdfExportRoute =
		typeof window !== 'undefined' && PDF_EXPORT_PAGE.match(window.location.pathname);
	const { isLoggedIn, isRovoLLMEnabled, accessStatus } = useSessionData();
	const isRovoEnabled =
		isRovoLLMEnabled && isLoggedIn && accessStatus !== AccessStatus.EXTERNAL_COLLABORATOR_ACCESS;

	// When loading confluence from the mobile app, we need the theme to respect the system color mode of the app
	const initialTheme = isMobileApp ? mobileAppTheme : undefined;

	// Initializing the theme in a non-blocking way, instead of using <ThemeContainer>, so
	// that the app does not flash and re-paint after the SPA loads on top of the SSR page.
	useInitializeThemeStateless(initialTheme);

	// Lite Mode™ check outside of the router
	// For certain pages supporting lite mode some functionality is not needed
	const isLite = isViewPageLite();

	// Check if app is being loaded in iFrame
	// We don't want to render the Rovo Chat wrapper if in iFrame
	const isIframe = typeof window !== 'undefined' && window.self !== window.top;

	const startPageLoadMetrics = useCallback(
		(routeName = '', ufoName = routeName, transitionId, contentId = '') => {
			startBrowserMetricsPageLoad(routeName, ufoName, transitionId, contentId);
			if (fg('platform_ufo_custom_cohort_data_migration')) {
				addUFOCustomCohortData('isDesktop', isDesktop);
			} else {
				addUFOCustomData({ isDesktop });
			}
		},
		[isDesktop],
	);

	const onRoute = useCallback(
		async (match, transitionId) => {
			// Reset window.__APOLLO_STATS__ on transition
			resetApolloStats();

			// transitionId = 0 when it is initial load, and 0 is falsey
			if (transitionId) {
				// The page can't be rendered by SSR if is a transition
				window['__SSR_RENDERED__'] = false;
				//hydrateRoot and related logic should not be enabled on transitions
				//as its only for ssr rendered page
				window['__HYDRATABLE__'] = false;
				releaseSSRedLegacyMacros();
				// Delayed content redirection is not needed on transitions
				window['__DELAYED_CONTENT_REDIRECTION__'] = {};
				if (debuggerEnabled) {
					clearAnalyticsDebugger();
				}
			} else {
				getMonitoringClient().incrementCounter('confluence-spa.react-initialized');
				window.performance.mark('CFP-63.route-manager-initialized');
			}
			setUniquePageLoadId(match?.name ?? '', match, transitionId, sessionData);

			startExperienceForRoute(match, transitionId, sessionData);

			void BMEventBus.stream([{ type: 'page-transition', ID: transitionId }]);

			const transitionPreloaderCurrentCallbackIDs =
				getGlobalTransitionPreloader().getCurrentRouteCallbackIds();
			const isSeamlessTransition = fg('confluence_frontend_transition_preloading_system')
				? transitionPreloaderCurrentCallbackIDs.includes(
						'live_doc_preloading_transition_callback_id',
					) && transitionId !== 0
				: false;

			//if the new editor metrics FF is on, start BM here only if the route is not edit page or if it is initial load, or an embedded editor.
			if (
				!isSeamlessTransition &&
				(!isEditPage(match?.name) || transitionId === 0 || isEmbeddedConfluence_DO_NOT_USE())
			) {
				startBrowserMetricsPageLoad(
					match?.name ?? '',
					match?.ufoName,
					transitionId,
					match?.params.contentId,
				);

				if (fg('platform_ufo_custom_cohort_data_migration')) {
					addUFOCustomCohortData('isDesktop', isDesktop);
				} else {
					addUFOCustomData({ isDesktop });
				}

				//If it is an embedded editor, start the BM right here since it does a full page reload
				if (isEditPage(match?.name) && isEmbeddedConfluence_DO_NOT_USE()) {
					ExperienceActionMeasures.markMeasureStart(EMBEDDED_EDIT_FMP_ACTION);
					ExperienceActionMeasures.markMeasureStart(EMBEDDED_EDIT_TTI_ACTION);
				}
			}

			// add SSR stats to initial page load only
			// we will be utilizing serverTime to track BE perf in Statsig
			if (transitionId === 0) {
				/** @type {Object<string, {startTime: number, duration: number} | undefined>} */
				const ssrMeasure = window['__SSR_MEASURES__'];
				const serverTime = ssrMeasure
					? Object.keys(ssrMeasure)
							.filter((key) => key.includes('prepare/preloadQuery/fetch:'))
							.reduce((total, key) => total + (ssrMeasure[key]?.duration ?? 0), 0)
					: 0;
				/** @type {number | undefined} */
				const serverCallCount = window['__FETCH_COUNT__'];

				if (serverTime && serverCallCount) {
					addUFOCustomData({ serverTime, serverCallCount });
				}
			}

			// mark edit experience after transition to new route is complete
			markCustomTransitionExperienceForRoute(match?.name);

			getMonitoringClient().updateContext({
				pageName: match?.name ?? 'unknown',
				transitionId,
				product: isEmbeddedConfluence_DO_NOT_USE() ? 'embeddedConfluence' : 'confluence',
			});
		},
		[sessionData, isDesktop],
	);

	/**
	 * Please note this App is a pure component.
	 * This means following components will NOT re-render when user navigates.
	 * Please be careful when adding any component that requires router information.
	 */
	return (
		<StrictMode>
			<ErrorBoundary attribution={Attribution.BACKBONE}>
				<ChangeboardingManager>
					<SSRRenderFailureNotification />
					<QueryBoundaryNotification />
					<ConnectedRouter history={browserHistory} context={ReactReduxContext}>
						{/* Components above this line do NOT re-render in SPA transition */}
						<Fragment>
							<LegacyBridge />
							{/* This needs be placed before content */}
							<ExternalShareContextProvider path={browserHistory.location.pathname}>
								<CreateDialogContextProvider>
									{/* These preloaders need to be passed here to avoid circular dependencies in full page editor */}
									<EditPageLoadingContextProvider
										nativeEditorComponentsPreloader={nativeEditorComponentsPreloader}
										livePageComponentsPreloader={preloadLivePagesComponents}
									>
										<SubscriptionsContextProvider>
											<RouteManager
												routeComponents={routeComponents}
												namedRoutes={namedRoutes}
												notFound={NotFound}
												onInitialize={onRoute}
												onTransition={onRoute}
												onPreload={onPreloadRoute}
												routePolicy={routePolicy}
												setBrowserHistoryListener={setBrowserHistoryListener}
												startPageLoadMetrics={startPageLoadMetrics}
											>
												{/* Components that relies on RoutesContext */}
												<LegacyCssOverrides />
												<Heartbeat />
												<NotificationLandedEvent />
												<TimeToAppIdle />
												<PostOfficeRouterListener />
												{!isLite && (
													<>
														<Dialogs />
														<LazyCreateDialogQueryRenderer />
														<LazyCreateDialogBlueprintWizardRenderer />
													</>
												)}
												{/* Rendering Rovo Chat without its UI to initialize the Global Rovo Store.
												ConfluenceConversationAssistant leverages the Router push function to handle internal navigation
												thus it is dependent on the Router and needs to be rendered within the RouteManager.
												*/}
												{!isIframe &&
													isRovoEnabled &&
													!isPdfExportRoute &&
													fg('aifc_create_enabled') && (
														<ConfluenceConversationAssistant>
															<></>
														</ConfluenceConversationAssistant>
													)}
											</RouteManager>
										</SubscriptionsContextProvider>
									</EditPageLoadingContextProvider>
								</CreateDialogContextProvider>
							</ExternalShareContextProvider>
							<SingleModalContainer />
							<NetworkStatusListener />
						</Fragment>
						{/* Components after this line do NOT re-render in SPA transition */}
					</ConnectedRouter>
					<ShortcutsProvider />
					<ErrorContainer />
					<ConnectSupport />
					<Flags />
					<InconsistentLicenseFlag />
					<LocalStorageQuotaSubscriber />

					{!isLite && (
						<>
							<CopySpacePermissionsForLegacyNewSpace />
							<EngagementMessages />
						</>
					)}
				</ChangeboardingManager>
			</ErrorBoundary>
		</StrictMode>
	);
}

App.propTypes = {
	sessionData: PropTypes.object.isRequired,
};

function onPreloadRoute({ url }, previousMatch, options) {
	void preloadComponentsNoStatsigSupport(url, true);

	if (options?.preloadingMode === PreloadingMode.onlyJS) return;

	void preloadQueryBasedOnRoute(url, true, previousMatch, options);

	if (options?.preloadingMode === PreloadingMode.onlyJSAndCriticalData) return;

	void preloadExtensionsData(url);
}
